import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Radio, RadioGroup, FormControlLabel, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { KanbanSquare, Plus, Star, Timer, Workflow, Clock } from "lucide-react";
import { EmptyState, GlassCard, HoverCard, T, TYPE, formatRelative, labelSx, primaryButtonSx, ghostButtonSx } from "../../user/complaints/shared";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { has } from "../ui";
import { MultiSelect, PersonPicker, Select, fieldSx } from "../inputs";

export const CreateBoardDialog = ({ open, onClose, project, onCreated }) => {
  const ctx = usePm();
  const [f, setF] = useState(null);
  const [filters, setFilters] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF({ name: "", key: "", type: "scrum", description: "", visibility: "project", memberIds: [], teamIds: [], sourceKind: "project", filterId: null });
    setError("");
    pm.filters(project._id).then(setFilters).catch(() => {});
  }, [open, project._id]);
  if (!open || !f) return null;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const b = await pm.createBoard(project._id, {
        name: f.name, key: f.key || undefined, type: f.type, description: f.description, visibility: f.visibility,
        memberIds: f.visibility === "users" ? f.memberIds : undefined, teamIds: f.visibility === "teams" ? f.teamIds : undefined,
        source: f.sourceKind === "filter" ? { kind: "filter", filterId: f.filterId } : { kind: "project" },
      });
      ctx.notify(`Board ${b.name} created`);
      onCreated(b);
    } catch (err) {
      setError(errorMessage(err, "Could not create the board"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: T.radiusCard } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Create board</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ ...labelSx, mb: 1 }}>Board type</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, mb: 2.5 }}>
          {[["scrum", Timer, "Scrum", "Plan work in sprints: backlog, sprint planning, active sprint, burndown and velocity."], ["kanban", Workflow, "Kanban", "Continuous flow without sprints: WIP limits, cycle time, cumulative flow."]].map(([v, Icon, title, desc]) => (
            <GlassCard key={v} onClick={() => set("type")(v)} sx={{ p: 1.5, cursor: "pointer", boxShadow: "none", borderColor: f.type === v ? T.brand : T.border, bgcolor: f.type === v ? T.brandSoft : "#fff" }}>
              <Stack direction="row" spacing={1} alignItems="center"><Icon size={16} color={T.brand} /><Typography sx={{ fontWeight: 700, fontSize: TYPE.small }}>{title}</Typography></Stack>
              <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary, mt: 0.5 }}>{desc}</Typography>
            </GlassCard>
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Board name *" autoFocus value={f.name} onChange={(e) => set("name")(e.target.value)} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Key" placeholder="auto" value={f.key} onChange={(e) => set("key")(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))} sx={fieldSx} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" multiline minRows={2} label="Description" value={f.description} onChange={(e) => set("description")(e.target.value)} sx={fieldSx} /></Grid>
          <Grid item xs={12}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Issue source</Typography>
            <RadioGroup row value={f.sourceKind} onChange={(e) => set("sourceKind")(e.target.value)}>
              <FormControlLabel value="project" control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>All issues of {project.key}</Typography>} />
              <FormControlLabel value="filter" control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>A saved filter</Typography>} />
            </RadioGroup>
            {f.sourceKind === "filter" && (
              filters.length ? <Select label="Saved filter" value={f.filterId} onChange={set("filterId")} options={filters.map((x) => ({ value: x._id, label: `${x.name}${x.visibility === "shared" ? " (shared)" : ""}` }))} />
                : <Alert severity="info">Save a filter in the Issues tab first; the board then shows exactly those issues.</Alert>
            )}
          </Grid>
          <Grid item xs={12} sm={6}><Select label="Who can see it" value={f.visibility} onChange={set("visibility")} options={[["project", "Everyone with access to the project"], ["users", "Selected people"], ["teams", "Selected teams"], ["private", "Only me (and board admins)"], ["organization", "Organisation-wide"]].map(([value, label]) => ({ value, label }))} /></Grid>
          {f.visibility === "users" && <Grid item xs={12} sm={6}><PersonPicker label="People" multiple value={f.memberIds} onChange={set("memberIds")} /></Grid>}
          {f.visibility === "teams" && <Grid item xs={12} sm={6}><MultiSelect label="Teams" value={f.teamIds} onChange={set("teamIds")} options={ctx.teams.map((t) => ({ value: t._id, label: t.name }))} /></Grid>}
        </Grid>
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 2 }}>Columns start from the project workflow; configure columns, WIP limits, swimlanes, cards and quick filters in Board settings.</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={busy || !f.name.trim() || (f.sourceKind === "filter" && !f.filterId)} onClick={submit}>{busy ? "Creating…" : "Create board"}</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Project → Boards: every board of the project, favourites first. */
const BoardsTab = ({ project }) => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const load = useCallback(() => pm.boardList(project._id).then((r) => setRows(r.data)).catch((err) => setError(errorMessage(err))), [project._id]);
  useEffect(() => {
    load();
  }, [load]);
  const canCreate = has(project, "board.manage") && !project.archivedAt;
  // A lone board: open it directly instead of a one-card list (Create board lives in the board's switcher).
  useEffect(() => {
    if (rows?.length === 1) navigate(`/projects/${project.key}/boards/${rows[0]._id}`, { replace: true });
  }, [rows, navigate, project.key]);
  const toggleFav = async (e, b) => {
    e.stopPropagation();
    try {
      await pm.setBoardPrefs(b._id, { favorite: !b.favorite });
      load();
    } catch (err) {
      ctx.fail(err);
    }
  };
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!rows || rows.length === 1) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  const sorted = [...rows].sort((a, b) => Number(b.favorite) - Number(a.favorite) || Number(b.myDefault) - Number(a.myDefault) || Number(b.isDefault) - Number(a.isDefault));
  const recent = rows.filter((b) => b.lastVisitedAt).sort((a, b) => new Date(b.lastVisitedAt) - new Date(a.lastVisitedAt)).slice(0, 4);
  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, flexGrow: 1 }}>{rows.length} board{rows.length === 1 ? "" : "s"} over the same project issues — each with its own columns, filters and rules.</Typography>
        {canCreate && <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setOpen(true)}>Create board</Button>}
      </Stack>
      {recent.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Clock size={13} color={T.inkMuted} /><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Recent:</Typography>
          {recent.map((b) => <Chip key={b._id} size="small" label={b.name} onClick={() => navigate(`/projects/${project.key}/boards/${b._id}`)} />)}
        </Stack>
      )}
      {!rows.length ? (
        <GlassCard><EmptyState icon={KanbanSquare} title="No boards you can see" description="Create a Scrum board for sprints or a Kanban board for continuous flow." /></GlassCard>
      ) : (
        <Grid container spacing={2}>
          {sorted.map((b) => (
            <Grid item key={b._id} xs={12} sm={6} lg={4}>
              <HoverCard sx={{ p: 2, cursor: "pointer" }} onClick={() => navigate(`/projects/${project.key}/boards/${b._id}`)}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  {b.type === "scrum" ? <Timer size={18} color={T.brand} /> : <Workflow size={18} color="#0BA5EC" />}
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography noWrap sx={{ fontWeight: 700 }}>{b.name}</Typography>
                    <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{b.key || "—"} · {b.type === "scrum" ? "Scrum" : "Kanban"} · {b.columns} columns · {b.visibility}</Typography>
                  </Box>
                  <Tooltip title={b.favorite ? "Remove from favourites" : "Add to favourites"}>
                    <IconButton size="small" onClick={(e) => toggleFav(e, b)}><Star size={16} fill={b.favorite ? "#F79009" : "none"} color={b.favorite ? "#F79009" : T.inkMuted} /></IconButton>
                  </Tooltip>
                </Stack>
                {b.description && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mt: 1 }} noWrap>{b.description}</Typography>}
                <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }}>
                  {b.isDefault && <Chip size="small" label="Project default" sx={{ height: 20, fontSize: "0.66rem" }} />}
                  {b.myDefault && <Chip size="small" label="My default" color="primary" variant="outlined" sx={{ height: 20, fontSize: "0.66rem" }} />}
                  {b.lastVisitedAt && <Typography sx={{ fontSize: "0.66rem", color: T.inkMuted, alignSelf: "center" }}>visited {formatRelative(b.lastVisitedAt)}</Typography>}
                </Stack>
              </HoverCard>
            </Grid>
          ))}
        </Grid>
      )}
      <CreateBoardDialog open={open} onClose={() => setOpen(false)} project={project} onCreated={(b) => { setOpen(false); navigate(`/projects/${project.key}/boards/${b._id}`); }} />
    </Box>
  );
};

export default BoardsTab;
